// The rule sets an auction can run under.
//
// The backend holds the authoritative copy in auction-backend/Common/auctionModes.js
// and is what actually enforces these limits. This copy drives what the screens
// show and which bid increments are offered, so keep the two in step. The same
// file lives in auction-frontend/src/config/auctionRules.js.
//
// The values below are defaults. An admin can override any of them per mode from
// the dashboard; overrides arrive already merged from the backend, but mergeRules
// is here so a partial or missing set still resolves to something complete.
//
// A `max` of 0 means "no limit" - the men's auction has never capped Indians.

export const AUCTION_MODES = {
  men: {
    key: "men",
    label: "Men's Auction",
    short: "Men",
    squad: { max: 25 },
    indians: { min: 0, max: 0 },
    overseas: { min: 0, max: 8 },
    // Raises in lakhs. Which tier applies depends on how high bidding has
    // already gone; `upTo: null` is the final, open-ended tier.
    bidTiers: [
      { upTo: 100, increments: [5, 10, 20] },
      { upTo: null, increments: [20, 50] },
    ],
  },
  women: {
    key: "women",
    label: "Women's Auction",
    short: "Women",
    squad: { max: 25 },
    indians: { min: 9, max: 12 },
    overseas: { min: 0, max: 15 },
    bidTiers: [{ upTo: null, increments: [5, 10, 20] }],
  },
};

export const DEFAULT_MODE = "men";

// The order the two modes are offered in, which is deliberately not the same
// thing as DEFAULT_MODE: women reads first, men is still what a new admin gets.
export const MODE_ORDER = ["women", "men"];

// What the editor will accept. Mirrors the backend's own limits.
export const LIMITS = {
  squadMax: { min: 1, max: 100 },
  perTeam: { min: 0, max: 100 },
  increment: { min: 1, max: 1000 },
  tiers: { min: 1, max: 5 },
  incrementsPerTier: { min: 1, max: 6 },
};

// Always returns a rule set, so an unexpected value can never blank a screen.
export const getRules = (mode) =>
  AUCTION_MODES[mode] || AUCTION_MODES[DEFAULT_MODE];

export const isWomensMode = (mode) => getRules(mode).key === "women";

const num = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

// Folds saved overrides over a mode's defaults. Anything missing falls back, so
// a partial set can never leave a rule undefined.
export const mergeRules = (mode, overrides) => {
  const base = getRules(mode);
  const o = overrides && typeof overrides === "object" ? overrides : {};

  const tiers = Array.isArray(o.bidTiers)
    ? o.bidTiers.filter((t) => t?.increments?.length)
    : [];

  return {
    key: base.key,
    label: base.label,
    short: base.short,
    squad: { max: num(o.squad?.max, base.squad.max) },
    indians: {
      min: num(o.indians?.min, base.indians.min),
      max: num(o.indians?.max, base.indians.max),
    },
    overseas: {
      min: num(o.overseas?.min, base.overseas.min),
      max: num(o.overseas?.max, base.overseas.max),
    },
    bidTiers: tiers.length ? tiers : base.bidTiers,
  };
};

// The raises a bidder may choose from, given where the bidding stands.
export const incrementsForBid = (rules, currentBid) => {
  const tiers = rules?.bidTiers?.length
    ? rules.bidTiers
    : getRules(rules?.key).bidTiers;
  const tier = tiers.find((t) => t.upTo === null || Number(currentBid) < t.upTo);
  return (tier || tiers[tiers.length - 1]).increments;
};

// Short lines describing a resolved rule set, for admin and bidder screens.
export const ruleSummary = (rules) => {
  const lines = [];

  if (rules.indians?.max) {
    lines.push(`Indians Min ${rules.indians.min} - Max ${rules.indians.max}`);
  }
  if (rules.overseas?.max) {
    lines.push(`Foreigners Max ${rules.overseas.max}`);
  }
  lines.push(`Squad Max ${rules.squad.max}`);

  const increments = [
    ...new Set((rules.bidTiers || []).flatMap((tier) => tier.increments)),
  ].sort((a, b) => a - b);
  lines.push(`Bids of ${increments.map((i) => `${i}L`).join(", ")}`);

  return lines;
};
